import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatChipsModule } from '@angular/material/chips'
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSelectModule } from '@angular/material/select'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { firstValueFrom } from 'rxjs'
import { ConfirmDialogComponent } from '../../../confirm-dialog/confirm-dialog.component'
import { TimestampPipe } from '../../../timestamp.pipe'
import { CallEntitiesOverview, CallEntitiesService, CallEntity, CallEntityInterval, CallEntityTemplateItem, parseTimestamp } from '../call-entities.service'
import { CallEntityReadiness, inheritsEntityScope, resolveCallEntityReadiness } from '../call-entity-readiness'

export type ManageCallEntitiesDialogData = {
  callId: number
}

type IntervalDraft = {
  callEntityId: number
  /** The agenda item the interval is the entity's discussion of; null for the entity as a whole. */
  templateItemId: number | null
  start: string
  end: string
  isInvalid: boolean
}

type OrderedTemplateItem = {
  item: CallEntityTemplateItem
  depth: number
}

@Component({
  selector: 'app-manage-call-entities-dialog',
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTooltip,
    NgTemplateOutlet,
    TimestampPipe,
    TranslateDirective,
    TranslatePipe,
  ],
  templateUrl: './manage-call-entities-dialog.component.html',
  styleUrl: './manage-call-entities-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ManageCallEntitiesDialogComponent {
  data: ManageCallEntitiesDialogData = inject(MAT_DIALOG_DATA)
  private callEntitiesService = inject(CallEntitiesService)
  private dialog = inject(MatDialog)
  private translateService = inject(TranslateService)

  overview$ = this.callEntitiesService.getOverview(this.data.callId)

  private createEntityMutation = this.callEntitiesService.createEntityMutation()
  private importFromAgendaMutation = this.callEntitiesService.importFromAgendaMutation()
  private renameEntityMutation = this.callEntitiesService.renameEntityMutation()
  private reorderEntitiesMutation = this.callEntitiesService.reorderEntitiesMutation()
  private deleteEntityMutation = this.callEntitiesService.deleteEntityMutation()
  private replaceIntervalsMutation = this.callEntitiesService.replaceIntervalsMutation()
  private updateTemplateItemScopeMutation = this.callEntitiesService.updateTemplateItemScopeMutation()
  private generateEntityMinutesMutation = this.callEntitiesService.generateEntityMinutesMutation()
  private generateAllEntityMinutesMutation = this.callEntitiesService.generateAllEntityMinutesMutation()

  newEntityName = ''
  intervalDraft: IntervalDraft | null = null

  readiness(overview: CallEntitiesOverview, entity: CallEntity): CallEntityReadiness {
    return resolveCallEntityReadiness(overview, entity)
  }

  hasEntitySpecificItems(overview: CallEntitiesOverview): boolean {
    return overview.templateItems.some((item) => item.isEntitySpecific)
  }

  canGenerateAny(overview: CallEntitiesOverview): boolean {
    return overview.entities.some((entity) => resolveCallEntityReadiness(overview, entity) === 'READY')
  }

  /** The template in reading order, each item with how deep it sits, for a list indented like the agenda. */
  orderTemplateItems(items: CallEntityTemplateItem[]): OrderedTemplateItem[] {
    const childrenOf = (parentId: number | null): CallEntityTemplateItem[] =>
      items.filter((item) => item.parentId === parentId).toSorted((first, second) => first.index - second.index)
    const walk = (parentId: number | null, depth: number): OrderedTemplateItem[] =>
      childrenOf(parentId).flatMap((item) => [{ item, depth }, ...walk(item.templateItemId, depth + 1)])
    return walk(null, 0)
  }

  inheritsEntityScope(item: CallEntityTemplateItem, items: CallEntityTemplateItem[]): boolean {
    return inheritsEntityScope(item, items)
  }

  async addEntity() {
    const name = this.newEntityName.trim()
    if (name === '') {
      return
    }
    await this.createEntityMutation.mutateAsync({ callId: this.data.callId, name })
    this.newEntityName = ''
  }

  async importFromAgenda() {
    await this.importFromAgendaMutation.mutateAsync({ callId: this.data.callId })
  }

  async renameEntity(entity: CallEntity, name: string) {
    const trimmedName = name.trim()
    if (trimmedName === '' || trimmedName === entity.name) {
      return
    }
    await this.renameEntityMutation.mutateAsync({ callId: this.data.callId, callEntityId: entity.id, name: trimmedName })
  }

  async moveEntity(entities: CallEntity[], index: number, offset: -1 | 1) {
    const targetIndex = index + offset
    if (targetIndex < 0 || targetIndex >= entities.length) {
      return
    }
    const callEntityIds = entities.map((entity, position) => {
      if (position === index) {
        return entities[targetIndex].id
      }
      if (position === targetIndex) {
        return entities[index].id
      }
      return entity.id
    })
    await this.reorderEntitiesMutation.mutateAsync({ callId: this.data.callId, callEntityIds })
  }

  async deleteEntity(entity: CallEntity) {
    const confirmed = await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          data: {
            title: this.translateService.instant('CALL_ENTITIES.DELETE_DIALOG_TITLE', { name: entity.name }),
            description: this.translateService.instant('CALL_ENTITIES.DELETE_DIALOG_DESCRIPTION'),
          },
        })
        .afterClosed()
    )
    if (confirmed !== true) {
      return
    }
    await this.deleteEntityMutation.mutateAsync({ callId: this.data.callId, callEntityId: entity.id })
  }

  intervalsOf(entity: CallEntity, templateItemId: number | null): CallEntityInterval[] {
    return entity.intervals.filter((interval) => interval.templateItemId === templateItemId)
  }

  isDrafting(entity: CallEntity, templateItemId: number | null): boolean {
    return this.intervalDraft !== null && this.intervalDraft.callEntityId === entity.id && this.intervalDraft.templateItemId === templateItemId
  }

  /** The entities an item's minutes are written for: those of the entity-specific item it is, or is under. */
  entitiesOf(item: CallEntityTemplateItem, overview: CallEntitiesOverview): CallEntity[] {
    const scopedItem = this.entitySpecificItemOf(item, overview.templateItems)
    if (scopedItem === null || scopedItem.applicableCallEntityIds.length === 0) {
      return overview.entities
    }
    return overview.entities.filter((entity) => scopedItem.applicableCallEntityIds.includes(entity.id))
  }

  private entitySpecificItemOf(item: CallEntityTemplateItem, items: CallEntityTemplateItem[]): CallEntityTemplateItem | null {
    if (item.isEntitySpecific) {
      return item
    }
    const parent = item.parentId === null ? null : (items.find((candidate) => candidate.templateItemId === item.parentId) ?? null)
    return parent === null ? null : this.entitySpecificItemOf(parent, items)
  }

  startIntervalDraft(entity: CallEntity, templateItemId: number | null) {
    this.intervalDraft = { callEntityId: entity.id, templateItemId, start: '', end: '', isInvalid: false }
  }

  cancelIntervalDraft() {
    this.intervalDraft = null
  }

  async saveIntervalDraft(entity: CallEntity, templateItemId: number | null) {
    const draft = this.intervalDraft
    if (draft === null || !this.isDrafting(entity, templateItemId)) {
      return
    }
    const startSeconds = parseTimestamp(draft.start)
    const endSeconds = parseTimestamp(draft.end)
    if (startSeconds === null || endSeconds === null || endSeconds <= startSeconds) {
      this.intervalDraft = { ...draft, isInvalid: true }
      return
    }
    const intervals = [...this.intervalsOf(entity, templateItemId).map(toIntervalInput), { startSeconds, endSeconds }].toSorted(
      (first, second) => first.startSeconds - second.startSeconds
    )
    await this.replaceIntervalsMutation.mutateAsync({ callId: this.data.callId, callEntityId: entity.id, templateItemId, intervals })
    this.intervalDraft = null
  }

  async removeInterval(entity: CallEntity, removed: CallEntityInterval) {
    const intervals = this.intervalsOf(entity, removed.templateItemId)
      .filter((interval) => interval.id !== removed.id)
      .map(toIntervalInput)
    await this.replaceIntervalsMutation.mutateAsync({ callId: this.data.callId, callEntityId: entity.id, templateItemId: removed.templateItemId, intervals })
  }

  async setEntitySpecific(item: CallEntityTemplateItem, isEntitySpecific: boolean) {
    await this.updateTemplateItemScopeMutation.mutateAsync({
      callId: this.data.callId,
      templateItemId: item.templateItemId,
      isEntitySpecific,
      applicableCallEntityIds: isEntitySpecific ? item.applicableCallEntityIds : [],
    })
  }

  async setApplicableEntities(item: CallEntityTemplateItem, applicableCallEntityIds: number[]) {
    await this.updateTemplateItemScopeMutation.mutateAsync({
      callId: this.data.callId,
      templateItemId: item.templateItemId,
      isEntitySpecific: true,
      applicableCallEntityIds,
    })
  }

  async generateEntityMinutes(entity: CallEntity) {
    await this.generateEntityMinutesMutation.mutateAsync({ callId: this.data.callId, callEntityId: entity.id })
  }

  async generateAllEntityMinutes() {
    await this.generateAllEntityMinutesMutation.mutateAsync({ callId: this.data.callId })
  }
}

function toIntervalInput(interval: CallEntityInterval) {
  return { startSeconds: interval.startSeconds, endSeconds: interval.endSeconds }
}
